import React, { useEffect, useState } from "react";
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase-config";
import { collection, getDocs } from "firebase/firestore";
import { motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import { withGuidePageRoute } from "../../data/blogPageHelpers";

/** Static card linking to the Meter Reading Guide page (not from Firestore).
 *  Commented out — guide is now a Type 2 blog in Firestore.
 */
// export const READING_GUIDE_POST = {
//   id: "reading-guide-static",
//   Caption: "Meter Reading Guide For On-Site Managers",
//   Body: "<h2>How to read electric, gas, and water meters</h2><p>This guide is designed to help on-site managers accurately read their utility meters. Follow standardized procedures for fair billing and resident trust.</p>",
//   Date: "2026-08-12",
//   Image: "/reading-guide/electric-analog.png",
//   route: "/reading-guide",
//   forceShowOnHome: true,
// };

/** Published blogs only, sorted newest-first by `Date`. */
export async function fetchPublishedBlogs() {
  const blogCollection = collection(db, "Blogs");
  const blogSnapshot = await getDocs(blogCollection);
  const blogList = blogSnapshot.docs.map((doc) =>
    withGuidePageRoute({
      ...doc.data(),
      id: doc.id,
    })
  );
  const today = new Date().toISOString().split("T")[0];
  const filteredBlogs = blogList.filter((blog) => {
    const blogDate = new Date(blog.Date).toISOString().split("T")[0];
    return blogDate <= today;
  });
  filteredBlogs.sort(
    (a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime()
  );
  // Static reading-guide card removed — served dynamically via Type 2 Blogs
  // return [READING_GUIDE_POST, ...filteredBlogs];
  return filteredBlogs;
}

/**
 * Home strip: always the newest-by-date post (skipping route/Type 2 cards),
 * plus any older posts with `forceShowOnHome === true`.
 * Archive is everything else (for the past modal).
 */
export function getHomeAndArchiveBlogs(sorted) {
  if (!sorted?.length) {
    return { homeBlogs: [], pastBlogs: [] };
  }
  const latestIdx = sorted.findIndex((b) => !b.route);
  const latest = latestIdx >= 0 ? sorted[latestIdx] : sorted[0];
  const forcedOlder = sorted.filter(
    (b) => b !== latest && b.forceShowOnHome === true
  );
  const homeBlogs = [latest, ...forcedOlder];
  const homeIds = new Set(homeBlogs.map((b) => b.id));
  const pastBlogs = sorted.filter((b) => !homeIds.has(b.id));
  return { homeBlogs, pastBlogs };
}

/** When `blogs` is passed, Firestore is not queried (parent owns loading). */
function BlogView({ blogs: blogsProp }) {
  const navigate = useNavigate();
  const controlled = blogsProp !== undefined;
  const [blogData, setBlogData] = useState(() =>
    controlled ? blogsProp : []
  );
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(!controlled);
  const [error, setError] = useState(null);

  const getBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await fetchPublishedBlogs();
      setBlogData(list);
    } catch (error) {
      console.error("Error fetching blog data: ", error);
      setError(error.message || "Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (controlled) {
      setBlogData(blogsProp);
      setLoading(false);
      setError(null);
      return;
    }
    getBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- standalone fetch once
  }, [controlled, blogsProp]);

  const extractSubtitleAndTrimBody = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    // Find and extract h1 or h2 tag
    const h1 = doc.querySelector("h1");
    const h2 = doc.querySelector("h2");

    let subtitle = "";
    if (h1) {
      subtitle = h1.textContent;
      h1.remove(); // Remove the h1 tag from the body
    } else if (h2) {
      subtitle = h2.textContent;
      h2.remove(); // Remove the h2 tag from the body
    }

    // Get the remaining body content (without the header)
    const trimmedBody = doc.body.innerHTML;

    return { subtitle, trimmedBody };
  };

  const handleCardClick = (blog) => {
    // Type 2 (or static reading guide): navigate to full page instead of modal
    if (blog.route) {
      navigate(blog.route);
      return;
    }
    // If it's a PDF and on mobile, open directly in new tab instead of modal
    if (blog.pdfUrl && isMobile) {
      window.open(blog.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setSelectedBlog(blog);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Show loading state
  if (loading) {
    return null; // Or return a loading spinner if you prefer
  }

  // Show error (optional - can remove if you don't want to show errors)
  if (error) {
    console.error("BlogView error:", error);
    // Return null to hide error, or uncomment below to show error message
    // return (
    //   <div className="bg-dark text-white p-3">
    //     <p>Error loading blogs: {error}</p>
    //   </div>
    // );
    return null;
  }

  // Return null if there are no blogs
  if (blogData.length === 0) {
    return null;
  }

  return (
    <div
      className="bg-dark"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        {blogData.map((blog, index) => {
          // Extract subtitle and body without the header tag
          // Use full body to ensure HTML parsing works correctly
          const { subtitle, trimmedBody } = extractSubtitleAndTrimBody(
            blog.Body
          );

          // Strip HTML for the card preview to prevent layout issues
          const plainTextBody = trimmedBody.replace(/<[^>]+>/g, " ");

          return (
            <Card
              as={motion.div}
              className="bg-dark text-white shadow"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: "easeOut",
              }}
              style={{
                width: "300px",
                height: "400px",
                margin: "15px",
                cursor: "pointer",
                borderRadius: "15px",
                overflow: "hidden",
                border: "none",
              }}
              key={blog.id ?? index}
              onClick={() => handleCardClick(blog)}
            >
              <Card.Img
                src={blog.Image}
                alt={blog.Caption}
                style={{
                  height: "100%",
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  objectFit: "cover",
                  zIndex: 0,
                }}
              />
              {/* Gradient Overlay for readability */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.1) 100%)",
                  zIndex: 1,
                }}
              ></div>

              <Card.ImgOverlay
                style={{
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <Card.Title
                  style={{
                    fontWeight: "bold",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
                  }}
                >
                  {blog.Caption}
                </Card.Title>
                {subtitle && (
                  <Card.Subtitle
                    className="mb-2 text-light"
                    style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}
                  >
                    {subtitle}
                  </Card.Subtitle>
                )}
                <Card.Text
                  style={{
                    textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                    fontSize: "0.9rem",
                  }}
                >
                  {plainTextBody.length > 100
                    ? `${plainTextBody.substring(0, 100)}...`
                    : plainTextBody}
                </Card.Text>
                <Card.Text
                  className="text-white-50"
                  style={{ fontSize: "0.8rem" }}
                >
                  {blog.Date}
                </Card.Text>
              </Card.ImgOverlay>
            </Card>
          );
        })}
      </div>

      {/* Modal to display detailed blog information */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        centered
        scrollable={true} // Ensures the modal body can scroll if content exceeds height
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedBlog && selectedBlog.Caption}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "60vh" }}>
          {selectedBlog && (
            <>
              {/* Check if it is a PDF or standard HTML blog */}
              {selectedBlog.pdfUrl ? (
                <div
                  style={{
                    width: "100%",
                    height: "75vh",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <iframe
                    src={`${selectedBlog.pdfUrl}#view=FitH`}
                    title={selectedBlog.Caption}
                    width="100%"
                    height="100%"
                    style={{ border: "none", borderRadius: "4px" }}
                  >
                    {/* Fallback for browsers that don't support iframes/pdf rendering */}
                    <p>
                      Your browser does not support PDFs.
                      <a
                        href={selectedBlog.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download the PDF
                      </a>
                      .
                    </p>
                  </iframe>
                  <div className="text-center mt-2">
                    <a
                      href={selectedBlog.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      Open PDF in new tab
                    </a>
                  </div>
                </div>
              ) : (
                /* Standard HTML Blog Rendering */
                <div dangerouslySetInnerHTML={{ __html: selectedBlog.Body }} />
              )}

              <p className="mt-3 text-muted">{selectedBlog.Date}</p>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* <div style={{ marginTop: "20px" }}>
        <Link to="/?allPosts=1">View All</Link>
      </div> */}
    </div>
  );
}

export default BlogView;
